import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "../components/layout/AuthLayout.js";
import { Button } from "../components/ui/Button.js";
import { Field } from "../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { apiPost, isApiError } from "../lib/api.js";
import { translateError } from "../lib/form.js";

const PASSWORD_MIN = 10;

const resetSchema = z
  .object({
    password: z.string().min(PASSWORD_MIN, "validation.passwordMin"),
    confirmPassword: z.string().min(1, "validation.required"),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "validation.passwordMismatch",
  });

type ResetForm = z.infer<typeof resetSchema>;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [done, setDone] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await apiPost<void>("/auth/reset-password", {
        token,
        password: values.password,
      });
      setDone(true);
    } catch (error) {
      if (isApiError(error) && error.fields && "token" in error.fields) {
        setTokenInvalid(true);
      } else {
        setError("root", { message: t("errors.generic") });
      }
    }
  });

  if (!token || tokenInvalid) {
    return (
      <AuthLayout title={t("auth.reset.title")}>
        <div className="space-y-4">
          <p
            role="alert"
            className="rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger"
          >
            {t("auth.reset.missingToken")}
          </p>
          <Link
            to="/forgot-password"
            className="inline-block text-sm font-medium text-brand hover:underline"
          >
            {t("auth.forgot.title")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        title={t("auth.reset.successTitle")}
        subtitle={t("auth.reset.successMessage")}
      >
        <Button as={Link} to="/login" size="large" className="w-full">
          {t("auth.reset.toLogin")}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.reset.title")}
      subtitle={t("auth.reset.subtitle")}
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field
          label={t("auth.reset.newPassword")}
          htmlFor="password"
          error={
            errors.password?.message
              ? t(errors.password.message, { min: PASSWORD_MIN })
              : undefined
          }
          required
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.password}
            {...register("password")}
          />
        </Field>
        <Field
          label={t("auth.reset.confirmPassword")}
          htmlFor="confirmPassword"
          error={translateError(errors.confirmPassword?.message, t)}
          required
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.confirmPassword}
            {...register("confirmPassword")}
          />
        </Field>
        {errors.root ? (
          <p
            role="alert"
            className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {errors.root.message}
          </p>
        ) : null}
        <Button
          type="submit"
          size="large"
          loading={isSubmitting || undefined}
          className="w-full"
        >
          {t("auth.reset.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
